import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable pagination control.
 * Props:
 *  - page: current 1-based page
 *  - totalPages: total page count
 *  - onPageChange: (newPage) => void
 *  - perPage: current per-page size (optional)
 *  - onPerPageChange: (newSize) => void (optional, shows dropdown if provided)
 *  - perPageOptions: array of selectable sizes (default [10, 25, 50, 100])
 *  - total: total item count (for display)
 *  - testIdPrefix: data-testid prefix
 */
export default function Paginator({
  page = 1,
  totalPages = 1,
  onPageChange,
  perPage,
  onPerPageChange,
  perPageOptions = [10, 25, 50, 100],
  total,
  testIdPrefix = "pager",
}) {
  if (totalPages <= 1 && !onPerPageChange) return null;

  const goPrev = () => onPageChange?.(Math.max(1, page - 1));
  const goNext = () => onPageChange?.(Math.min(totalPages, page + 1));

  return (
    <div className="flex items-center justify-between gap-3 mt-4 flex-wrap" data-testid={`${testIdPrefix}-wrap`}>
      <div className="flex items-center gap-2 text-[11px] text-[#64748B]">
        {typeof total === "number" && <span>{total.toLocaleString()} total</span>}
        {onPerPageChange && (
          <>
            <span className="text-[#475569]">·</span>
            <span>Show</span>
            <select
              value={perPage}
              onChange={(e) => onPerPageChange(parseInt(e.target.value, 10))}
              className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-1.5 py-0.5 text-[11px]"
              data-testid={`${testIdPrefix}-per-page`}>
              {perPageOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>per page</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={goPrev}
          disabled={page <= 1}
          data-testid={`${testIdPrefix}-prev`}
          className="p-1.5 text-[#94A3B8] hover:text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs font-mono-game text-[#F8FAFC] px-2 min-w-[80px] text-center" data-testid={`${testIdPrefix}-info`}>
          {page} / {totalPages}
        </span>
        <button
          onClick={goNext}
          disabled={page >= totalPages}
          data-testid={`${testIdPrefix}-next`}
          className="p-1.5 text-[#94A3B8] hover:text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
